import React from "react";
import { ExternalLink } from "../../../components/ExternalLink";

import SecureScoreProfileConstants from "./secure-score-profile-constants";
import { deserializeCompliance } from "./secure-score-profile-utils";

/** @typedef {import ('./constants').SecureScoreProfileType} SecureScoreProfileType */

const DEFAULT_NEGATIVE_MDO_STATUS_ID =
  "SECURE.STATUS_DESCRIPTION.NEGATIVE.MDO_DEFAULT";

const hasMessage = (intl, id) =>
  Boolean(id) && Object.prototype.hasOwnProperty.call(intl?.messages ?? {}, id);

const getMessageValue = (intl, id) => {
  if (!hasMessage(intl, id)) {
    return undefined;
  }

  return intl?.messages?.[id];
};

const resolveMessageId = (intl, id) => (hasMessage(intl, id) ? id : null);

const formatExistingMessage = (intl, id, fallbackId) => {
  const resolvedId =
    resolveMessageId(intl, id) ?? resolveMessageId(intl, fallbackId);

  if (!resolvedId) {
    return "";
  }

  const messageValue = getMessageValue(intl, resolvedId);
  if (messageValue === "") {
    return "";
  }

  return intl.formatMessage({
    id: resolvedId,
  });
};

const secureScoreProfilePostureConfigs = ({
  AUGMENTT_POSTURE_STATES,
  COMPLIANCE_TYPES,
  SHORTCUTS,
}) => {
  const secureScoreProfileConstants = new SecureScoreProfileConstants({
    COMPLIANCE_TYPES,
  });

  const { SECURE_SCORE_PROFILE_TYPES } = secureScoreProfileConstants;

  const keys = Object.keys(SECURE_SCORE_PROFILE_TYPES);
  const result = {};

  keys.forEach(
    /* @type {SecureScoreProfileType} */ (key) => {
      const type = SECURE_SCORE_PROFILE_TYPES[key];
      result[type] = {
        rationale: ({ intl }) => {
          const externalLinkId = `SECURE.RATIONALE.EXTERNAL.LINK.${key}`;
          const externalLink = formatExistingMessage(intl, externalLinkId);

          const rationaleId = `SECURE.RATIONALE.${key}`;
          const rationale = formatExistingMessage(intl, rationaleId);

          return (
            <>
              {rationale}
              {externalLink && (
                <div>
                  <ExternalLink href={externalLink} label={externalLink} />
                </div>
              )}
            </>
          );
        },
        statusDetailsFormatter: (row, intl) => {
          const positiveId = `SECURE.STATUS_DESCRIPTION.POSITIVE.${key}`;
          const positiveMessage = formatExistingMessage(intl, positiveId);

          const negativeId = `SECURE.STATUS_DESCRIPTION.NEGATIVE.${key}`;
          const negativeFallbackId = key.startsWith("MDO_")
            ? DEFAULT_NEGATIVE_MDO_STATUS_ID
            : null;
          const negativeMessage = formatExistingMessage(
            intl,
            negativeId,
            negativeFallbackId
          );
          return row.status === AUGMENTT_POSTURE_STATES.COMPLIANT
            ? positiveMessage
            : negativeMessage;
        },
        compliance: ({ intl }) => {
          const complianceId = `SECURE.COMPLIANCE.${key}`;
          const complianceFormattedMessage = formatExistingMessage(
            intl,
            complianceId
          );
          const compliance = complianceFormattedMessage
            ? deserializeCompliance(
                complianceFormattedMessage,
                COMPLIANCE_TYPES
              )
            : COMPLIANCE_TYPES.NONE;
          return compliance;
        },
        shortcut: SHORTCUTS.DEFENDER,
      };
    }
  );

  return Object.freeze(result);
};

export { secureScoreProfilePostureConfigs };
